import { useState, useRef, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useLang } from '../contexts/LangContext'

// The support form: type, message and an optional screenshot, sent to the
// send-feedback edge function. Shared by the floating FeedbackWidget and the
// "Precisa de ajuda?" card on the Visão geral, so both send exactly the same.

type FeedbackType = 'bug' | 'suggestion' | 'other'
type Status = 'idle' | 'sending' | 'success' | 'error' | 'img-too-large'

const MAX_IMAGE_BYTES = 8 * 1024 * 1024 // 8 MB guard

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload  = () => resolve((reader.result as string).split(',')[1]) // strip data:… prefix
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export function FeedbackForm({ onSent, onSendingChange }: {
  /** Called 2.2 s after a successful send, once the thank-you message was shown. */
  onSent?: () => void
  onSendingChange?: (sending: boolean) => void
}) {
  const { t } = useLang()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [type, setType]       = useState<FeedbackType>('bug')
  const [message, setMessage] = useState('')
  const [image, setImage]     = useState<{ file: File; preview: string } | null>(null)
  const [status, setStatus]   = useState<Status>('idle')

  useEffect(() => { onSendingChange?.(status === 'sending') }, [status, onSendingChange])

  const TYPES: { value: FeedbackType; label: string }[] = [
    { value: 'bug',        label: t('feedback.typeBug') },
    { value: 'suggestion', label: t('feedback.typeSuggestion') },
    { value: 'other',      label: t('feedback.typeOther') },
  ]

  function handleImageSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > MAX_IMAGE_BYTES) {
      setStatus('img-too-large')
      e.target.value = ''
      return
    }
    const preview = URL.createObjectURL(file)
    setImage({ file, preview })
    if (status === 'img-too-large' || status === 'error') setStatus('idle')
    e.target.value = ''
  }

  function removeImage() {
    if (image) URL.revokeObjectURL(image.preview)
    setImage(null)
    if (status === 'img-too-large') setStatus('idle')
  }

  async function handleSubmit() {
    if (!message.trim() || status === 'sending') return
    setStatus('sending')
    try {
      await supabase.auth.refreshSession()
      const { data: { session } } = await supabase.auth.getSession()

      const body: Record<string, unknown> = {
        type,
        message: message.trim(),
        page_url: window.location.href,
      }

      if (image) {
        body.image_base64 = await fileToBase64(image.file)
        body.image_name   = image.file.name
        body.image_type   = image.file.type
      }

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-feedback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify(body),
        },
      )
      if (!res.ok) throw new Error('send failed')
      setStatus('success')
      if (image) URL.revokeObjectURL(image.preview)
      setTimeout(() => {
        setStatus('idle')
        setMessage('')
        setImage(null)
        onSent?.()
      }, 2200)
    } catch {
      setStatus('error')
    }
  }

  const canSubmit = message.trim().length > 0 && status !== 'sending'

  if (status === 'success') {
    return (
      <div style={{ textAlign: 'center', padding: '24px 0' }}>
        <div style={{ fontSize: '32px', marginBottom: '10px' }}>✅</div>
        <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>
          {t('feedback.success')}
        </p>
      </div>
    )
  }

  return (
    <>
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageSelect} />

      {/* Type pills */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {TYPES.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            onClick={() => setType(value)}
            aria-pressed={type === value}
            style={{
              padding: '5px 12px',
              borderRadius: '999px',
              border: '1px solid',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.12s',
              borderColor: type === value ? 'var(--brand)' : 'var(--border)',
              backgroundColor: type === value ? 'var(--tone-green-bg)' : 'transparent',
              color: type === value ? 'var(--brand)' : 'var(--text-secondary)',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <textarea
        value={message}
        onChange={e => { setMessage(e.target.value); if (status === 'error') setStatus('idle') }}
        placeholder={t('feedback.placeholder')}
        aria-label={t('feedback.placeholder')}
        rows={4}
        disabled={status === 'sending'}
        style={{
          width: '100%',
          resize: 'none',
          borderRadius: '11px',
          border: '1px solid var(--border)',
          backgroundColor: 'var(--bg-subtle)',
          color: 'var(--text-primary)',
          fontSize: '13px',
          lineHeight: '1.6',
          padding: '10px 12px',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />

      <div style={{ marginTop: '8px' }}>
        {image ? (
          <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
            <img
              src={image.preview}
              alt={t('feedback.imageAlt')}
              style={{ width: '100%', maxHeight: '140px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border)', display: 'block' }}
            />
            <button
              type="button"
              onClick={removeImage}
              disabled={status === 'sending'}
              title={t('feedback.removeImage')}
              aria-label={t('feedback.removeImage')}
              style={{
                position: 'absolute', top: '6px', right: '6px', width: '22px', height: '22px', borderRadius: '50%',
                backgroundColor: 'rgba(0,0,0,0.65)', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
              }}
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <span style={{ display: 'block', marginTop: '4px', fontSize: '11px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
              📎 {image.file.name}
            </span>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={status === 'sending'}
            className="hover:border-[var(--brand)] hover:text-[var(--brand)]"
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
              padding: '9px 12px', borderRadius: '11px', border: '1px dashed var(--border)',
              backgroundColor: 'transparent', color: 'var(--text-secondary)',
              fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', transition: 'border-color 0.12s, color 0.12s',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" />
            </svg>
            {t('feedback.attachImage')}
            <span style={{ marginLeft: 'auto', fontWeight: 500, fontSize: '11px', color: 'var(--text-muted)' }}>8 MB</span>
          </button>
        )}

        {status === 'img-too-large' && (
          <p role="alert" style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--tone-red)' }}>{t('feedback.imageTooLarge')}</p>
        )}
        {status === 'error' && (
          <p role="alert" style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--tone-red)' }}>{t('feedback.error')}</p>
        )}
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        style={{
          marginTop: '10px', width: '100%', padding: '10px', borderRadius: '11px', border: 'none',
          backgroundColor: 'var(--brand)', opacity: canSubmit ? 1 : 0.45, color: 'white',
          fontSize: '13.5px', fontWeight: 700, cursor: canSubmit ? 'pointer' : 'not-allowed', transition: 'opacity 0.15s',
        }}
      >
        {status === 'sending' ? t('feedback.sending') : t('feedback.submit')}
      </button>
    </>
  )
}
